/* global menuData */
import { map } from '@/components/helpers/utils'
import { Fragment } from 'preact'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { handleize, select, selectAll, removeClass, addClass, on, hasClass } from 'helpers/dom'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import register from 'preact-custom-element'
import useDetectBreakpoint from 'hooks/useDetectBreakpoint'
import Icon from 'snippets/icon/icon'
import Logo from 'snippets/logo/logo'
import Image from 'snippets/image/image'
import CarouselSwiper from 'modules/carousel-swiper/carousel-swiper'
import './mega-menu.css'
import throttle from 'lodash.throttle'

let devtools
if (process.env.NODE_ENV === 'development') {
  devtools = require('preact/devtools')
}

const selectors = {
  ACTIVE_CLASS: 'active',
  ACTIVE_MENU_CLASS: 'active-menu',
  HOVER_CLASS: 'hover',
  MENU_TRIGGER_SELECTOR: '.js-menu-trigger',
  MENU_ITEM_SELECTOR: '.js-menu-item',
  MENU_PANEL_SELECTOR: '.js-menu-sub-panel',
  MEGA_MENU_WRAPPER_SELECTOR: '.js-mega-menu',
  SUB_ACTIVE_CLASS: 'active-sub',
  MEGA_MENU_INNER_SELECTOR: '.mega-menu-inner',
  MEGA_MENI_INNER_CONTENT_SELECTOR: '.mega-menu-inner-content',
  DAY_TIME: 200,
  LEVEL_DEFAULT: 0,
  PAGE_HEADER_SELECTOR: '.js-page-header',
  IS_PIN_CLASS: 'is-pin',
  IS_UNPIN_CLASS: 'is-unpin',
  IS_PIN_MENU_CLASS: 'is-pin-menu',
  MENU_CONTENT_SELECTOR: '.js-menu-content',
  SITE_HEADER_SELECTOR: '.js-site-header',
  MENU_SUB_CONTENT_SELECTOR: '.js-menu-sub-content'
}

function PostcodeButton () {
  const [postcode, setPostcode] = useState(() => {
    try { return localStorage.getItem('location_postcode') || null } catch (e) { return null }
  })

  useEffect(() => {
    const handleUpdate = (e) => setPostcode(e.detail.postcode)
    window.addEventListener('postcode:updated', handleUpdate)
    return () => window.removeEventListener('postcode:updated', handleUpdate)
  }, [])

  const openModal = () => {
    const modal = select('modal-dialog[data-target="PostcodeSelector"]')
    if (modal) modal.show()
  }

  if (!postcode) {
    return (
      <button
        type="button"
        className="flex items-center w-full gap-3 px-4 py-3 text-xs font-medium bg-[#F6F6EE] text-current outline-none cursor-pointer hover:opacity-80"
        onclick={openModal}
        aria-label={menuData?.postcode?.set_postcode}
      >
        <span className="flex items-center justify-center flex-shrink-0" aria-hidden="true">
          <Icon name="icon-truck-thin" viewBox="0 0 20 20" className="w-[20px] h-[20px]" />
        </span>
        <span className='underline'>{menuData?.postcode?.set_postcode}</span>
      </button>
    )
  }

  return (
    <div className="flex items-center w-full gap-3 px-4 py-3 bg-[#F6F6EE] text-sm font-medium text-current">
      <span className="flex items-center justify-center flex-shrink-0" aria-hidden="true">
        <Icon name="icon-truck-thin" viewBox="0 0 20 20" className="w-[20px] h-[20px]" />
      </span>
      <span className='text-xs font-normal'>{menuData?.postcode?.delivering_to} ({postcode})</span>
      <button
        type="button"
        className="flex-shrink-0 ml-auto text-xs font-normal underline bg-transparent outline-none cursor-pointer underline-offset-2 hover:opacity-80"
        onclick={openModal}
        aria-label={`${menuData?.postcode?.change} postcode`}
      >
        {menuData?.postcode?.change}
      </button>
    </div>
  )
}

function MegaMenu () {
  const megaMenuRef = useRef(null)
  const menuInnerRef = useRef(null)
  const megaMenuInnerContent = useRef(null)
  const [mainMenu, setMainMenu] = useState([])
  const [compileData, setCompileData] = useState([])
  const [hovered, setHovered] = useState(false)
  const [showGradient, setShowGradient] = useState(false)
  const [menuScrollIndicator, setMenuScrollIndicator] = useState({ height: 0, top: 0, visible: false })

  const { isMobileLarge } = useDetectBreakpoint()
  const menuItems = selectAll(selectors.MENU_ITEM_SELECTOR)
  const siteHeaderEl = select(selectors.SITE_HEADER_SELECTOR)

  const checkHeaderHeightAndApplyResponsive = () => {
    if (!siteHeaderEl || !megaMenuRef.current || isMobileLarge) {
      return
    }

    const headerHeight = siteHeaderEl.offsetHeight
    const megaMenuEl = megaMenuRef.current
    const hasResponsiveClass = hasClass('is-responsive', megaMenuEl)

    if (headerHeight === 169 && !hasResponsiveClass) { // Header height is 169px if there's stacked menu
      addClass('is-responsive', megaMenuEl)
    } else if (headerHeight === 137 && hasResponsiveClass) { // Header height is 137px if there's no stacked menu
      removeClass('is-responsive', megaMenuEl)
    }
  }

  const closeMegaMenu = () => {
    if (!megaMenuRef.current) {
      return
    }
    setMenuScrollIndicator(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev))
    const menuSubPanels = selectAll(selectors.MENU_PANEL_SELECTOR, megaMenuRef.current)
    closeAllMenuSubPanels()
    removeClass(selectors.ACTIVE_CLASS, megaMenuRef.current)
    removeClass(selectors.ACTIVE_CLASS, menuItems)
    removeClass(selectors.ACTIVE_CLASS, menuSubPanels)
    removeClass(selectors.ACTIVE_MENU_CLASS, siteHeaderEl)
    setTimeout(() => {
      removeClass(`${selectors.SUB_ACTIVE_CLASS}-0`, megaMenuInnerContent.current)
    }, 400)
    clearAllBodyScrollLocks()
  }

  const openMegaMenu = () => {
    if (!megaMenuRef.current) {
      return
    }
    const jsPageHeader = select(selectors.PAGE_HEADER_SELECTOR)
    if (jsPageHeader) {
      if (hasClass(selectors.IS_PIN_CLASS, jsPageHeader) && !isMobileLarge) {
        addClass(selectors.IS_PIN_MENU_CLASS, megaMenuRef.current)
      } else {
        removeClass(selectors.IS_PIN_MENU_CLASS, megaMenuRef.current)
      }

      if (hasClass(selectors.IS_UNPIN_CLASS, jsPageHeader) && !isMobileLarge) {
        addClass(selectors.IS_PIN_MENU_CLASS, megaMenuRef.current)
      } else {
        removeClass(selectors.IS_UNPIN_CLASS, megaMenuRef.current)
      }
    }
    addClass(selectors.ACTIVE_MENU_CLASS, siteHeaderEl)
    addClass(selectors.ACTIVE_CLASS, megaMenuRef.current)
    disableBodyScroll(menuInnerRef.current)
  }

  const addEventMegaMenuDesktop = () => {
    if (isMobileLarge || !megaMenuRef.current) {
      return
    }
    on('mouseenter', (e) => {
      setHovered(true)
    }, megaMenuRef.current)
    on('mouseleave', (e) => {
      setHovered(false)
    }, menuInnerRef.current)
  }

  const addEventMegaMenu = () => {
    const menuTrigger = selectAll(selectors.MENU_TRIGGER_SELECTOR)
    menuTrigger.forEach(item => {
      item.addEventListener('click', openMegaMenu)
    })
    addEventMegaMenuDesktop()
  }

  const setDataMenu = () => {
    const compileDataChildren = menuData?.mainMenu.children.map(item => (
      {
        ...item,
        children: [
          ...item.children,
          ...menuData?.blocks.find(block => handleize(block.title) === handleize(item.title))?.menu || []
        ],
        block: menuData?.blocks.find(block => handleize(block.title) === handleize(item.title)) || {}
      }
    ))

    const _compileData = {
      ...menuData?.mainMenu,
      children: [...compileDataChildren]
    }

    const _mainMenu = menuData?.settings.hideLevel1 ? _compileData.children.find(menu => menu.children.length > 0) : { ..._compileData }
    setMainMenu(_mainMenu)
    setCompileData(_compileData)
  }

  const handleMegaMenu = () => {
    if (hovered) {
      const menuSubcontent = select(selectors.MENU_SUB_CONTENT_SELECTOR)
      if (!menuSubcontent) {
        return
      }
      menuInnerRef.current.style.height = menuSubcontent.scrollHeight + 'px'
      openMegaMenu()
    } else {
      menuInnerRef.current.style.height = 0
      closeMegaMenu()
    }
  }

  useEffect(() => {
    setDataMenu()
  }, [])

  useEffect(() => {
    addEventMegaMenu()
    if (isMobileLarge || compileData?.children?.length === undefined) {
      return
    }
    compileData?.children.forEach(item => {
      const menuItem = select(`.js-menu-item[data-title="${handleize(item.title)}"]`)
      on('mouseenter', (e) => {
        if (item?.children.length > 0) {
          removeClass(selectors.ACTIVE_CLASS, menuItems)
          addClass(selectors.ACTIVE_CLASS, menuItem)
          setMainMenu(item)
          setHovered(true)
        }
        removeClass(selectors.HOVER_CLASS, menuItems)
        addClass(selectors.HOVER_CLASS, menuItem)
      }, menuItem)
      on('mouseleave', (e) => {
        if (item?.children.length > 0) setHovered(false)
        removeClass(selectors.HOVER_CLASS, menuItems)
      }, menuItem)
    })
  }, [compileData])

  useEffect(() => {
    const timer = setTimeout(handleMegaMenu, selectors.DAY_TIME)
    return () => {
      clearTimeout(timer)
    }
  }, [hovered])

  const evaluateGradientVisibility = useCallback(() => {
    const menuInnerEl = menuInnerRef.current
    if (!menuInnerEl) {
      setShowGradient(false)
      setMenuScrollIndicator(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev))
      return
    }

    if (!isMobileLarge) {
      setShowGradient(false)
      setMenuScrollIndicator(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev))
      return
    }

    const { scrollHeight, clientHeight, scrollTop } = menuInnerEl
    const hasScrollableContent = scrollHeight > clientHeight + 1
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

    setShowGradient(hasScrollableContent && !isAtBottom)

    if (!hasScrollableContent) {
      setMenuScrollIndicator(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev))
      return
    }

    const scrollableDistance = scrollHeight - clientHeight
    const minThumbSize = 32
    const thumbHeight = scrollableDistance <= 0 ? clientHeight : Math.max((clientHeight / scrollHeight) * clientHeight, minThumbSize)
    const maxThumbOffset = Math.max(clientHeight - thumbHeight, 0)
    const thumbTop = scrollableDistance <= 0 ? 0 : Math.min((scrollTop / scrollableDistance) * maxThumbOffset, maxThumbOffset)

    setMenuScrollIndicator({
      height: thumbHeight,
      top: thumbTop,
      visible: true
    })
  }, [isMobileLarge])

  useEffect(() => {
    const menuInnerEl = menuInnerRef.current
    if (!menuInnerEl) {
      setShowGradient(false)
      setMenuScrollIndicator(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev))
      return
    }

    const throttledEvaluateGradient = throttle(() => {
      evaluateGradientVisibility()
    }, 100)

    requestAnimationFrame(() => {
      evaluateGradientVisibility()
    })

    menuInnerEl.addEventListener('scroll', throttledEvaluateGradient)

    const resizeObserver = new ResizeObserver(() => {
      throttledEvaluateGradient()
    })
    resizeObserver.observe(menuInnerEl)

    return () => {
      menuInnerEl.removeEventListener('scroll', throttledEvaluateGradient)
      resizeObserver.disconnect()
      if (typeof throttledEvaluateGradient.cancel === 'function') {
        throttledEvaluateGradient.cancel()
      }
    }
  }, [evaluateGradientVisibility, mainMenu, hovered])

  useEffect(() => {
    on('resize', () => {
      if (window.innerWidth < 1024) {
        setDataMenu()
      }
    }, window)
  }, [])

  useEffect(() => {
    if (!siteHeaderEl || isMobileLarge) {
      return
    }

    checkHeaderHeightAndApplyResponsive()

    const resizeObserver = new ResizeObserver(throttle(() => {
      checkHeaderHeightAndApplyResponsive()
    }, 100))

    resizeObserver.observe(siteHeaderEl)

    return () => {
      resizeObserver.disconnect()
    }
  }, [siteHeaderEl, isMobileLarge])

  return mainMenu?.children?.length > 0 && (
    <div ref={megaMenuRef} className="mega-menu js-mega-menu">
      <div className="mega-menu-overlay" onclick={closeMegaMenu}></div>
      <div className="container mega-menu-container">
        <div className="mega-menu-inner">
          <div ref={megaMenuInnerContent} className="mega-menu-inner-content">
            {isMobileLarge && (
              <div className="flex items-center justify-between w-full px-4 py-3">
                <Logo settings={globalSettings} />
                <button type="button" onclick={closeMegaMenu} aria-label="Close Mega Menu">
                  <Icon name="icon-close" viewBox="0 0 20 20" strokeWidth="1.6" className='w-5 h-5 text-grey-500' />
                </button>
              </div>
            )}
            {isMobileLarge && <PostcodeButton />}
            <div ref={menuInnerRef} className={'mega-menu-content w-full h-auto bg-white overflow-y-auto transition-[height] duration-300 delay-100 ease-in-out lg-max:!h-auto js-menu-content'} style="height: 0">
              <div className={`w-full ${mainMenu?.children?.length > 8 ? 'scrollbar-custom-inner' : ''}`}>
                {isMobileLarge ? (
                  mainMenu?.children?.map((item, index) => item && (
                    <MenuSubItem settings={menuData?.settings} item={item} key={index} />
                  ))
                ) : (
                  <MenuSubItem settings={menuData?.settings} item={mainMenu} />
                )}
              </div>
              <MenuSecondary />
              {isMobileLarge && menuScrollIndicator.visible && (
                <div className="custom-scrollbar-track lg:hidden" aria-hidden="true">
                  <span
                    className="custom-scrollbar-thumb"
                    style={{ height: `${menuScrollIndicator.height}px`, transform: `translateY(${menuScrollIndicator.top}px)` }}
                  ></span>
                </div>
              )}
            </div>
            {isMobileLarge && showGradient && (
              <div className="mega-menu-content-gradient lg:hidden"></div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function MenuSubItem ({ settings, item }) {
  const { isMobileLarge } = useDetectBreakpoint()
  const settingsType = isMobileLarge ? settings?.type : settings?.typeDesktop
  const type = settingsType[`level${item.level}`]
  const renderMenuSubItem = (item) => {
    switch (type) {
      case 'panel':
        return <MenuSubPanel settings={settings} item={item} />
      case 'accordion':
        return <MenuSubAccordion settings={settings} item={item} />
      case 'content':
        return <MenuSubContent item={item} />
      default:
        return <MenuSubPanel settings={settings} item={item} />
    }
  }

  return item?.children.length === 0 ? (
    <a
      className="items-center justify-start gap-4 menu-sub-title"
      href={item.url}
      title={item.title}
    >
      {item?.block?.icon && (
        <div class="flex justify-center items-center truncate w-6 h-6">
          <Image
            imageUrl={[item?.block?.icon]}
            className="w-full h-auto"
            alt={`Menu subitem ${item?.block?.title}`}
            width="24"
            height="24"
          />
        </div>
      )}
      {item.title}
    </a>
  ) : renderMenuSubItem(item)
}

function closeAllMenuSubPanels (level = 1) {
  while (level < 3) {
    const menuSubPanels = selectAll(`.js-menu-sub-panel[data-level="${level}"]`)
    const btnOpenSubMenu = selectAll(`.js-button-open-sub-menu[data-level="${level}"]`)
    btnOpenSubMenu.forEach(btn => btn && btn.classList.remove(selectors.SUB_ACTIVE_CLASS))
    menuSubPanels.forEach(panel => panel && panel.classList.remove(selectors.ACTIVE_CLASS))
    level++
  }
  clearAllBodyScrollLocks()
}

function MenuSubPanel ({ settings, item }) {
  const [carouselInstance, setCarouselInstance] = useState(null)
  const [showPanelGradient, setShowPanelGradient] = useState(false)
  const [panelGradientOffset, setPanelGradientOffset] = useState(0)
  const [panelScrollIndicator, setPanelScrollIndicator] = useState({ height: 0, top: 0, visible: false })
  const panelRef = useRef(null)
  const panelInnerRef = useRef(null)
  const panelFooterRef = useRef(null)
  const { isMobileLarge } = useDetectBreakpoint()

  const evaluatePanelGradientVisibility = useCallback(() => {
    const panelContentEl = panelInnerRef.current

    if (!panelContentEl || !isMobileLarge) {
      setShowPanelGradient(false)
      setPanelScrollIndicator(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev))
      return
    }

    const { scrollHeight, clientHeight, scrollTop } = panelContentEl
    const hasOverflow = scrollHeight > clientHeight + 1
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1

    setShowPanelGradient(hasOverflow && !atBottom)

    if (!hasOverflow) {
      setPanelScrollIndicator(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev))
      return
    }

    const scrollableDistance = scrollHeight - clientHeight
    const minThumbSize = 32
    const thumbHeight = scrollableDistance <= 0 ? clientHeight : Math.max((clientHeight / scrollHeight) * clientHeight, minThumbSize)
    const maxThumbOffset = Math.max(clientHeight - thumbHeight, 0)
    const thumbTop = scrollableDistance <= 0 ? 0 : Math.min((scrollTop / scrollableDistance) * maxThumbOffset, maxThumbOffset)

    setPanelScrollIndicator({
      height: thumbHeight,
      top: thumbTop,
      visible: true
    })
  }, [isMobileLarge])

  useEffect(() => {
    const panelContentEl = panelInnerRef.current
    if (!panelContentEl || !isMobileLarge) {
      setShowPanelGradient(false)
      setPanelScrollIndicator(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev))
      return
    }

    const handleScroll = () => {
      evaluatePanelGradientVisibility()
    }

    let resizeObserver

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        evaluatePanelGradientVisibility()
      })
      resizeObserver.observe(panelContentEl)
    }

    requestAnimationFrame(() => {
      evaluatePanelGradientVisibility()
    })

    panelContentEl.addEventListener('scroll', handleScroll)

    return () => {
      panelContentEl.removeEventListener('scroll', handleScroll)
      resizeObserver?.disconnect()
    }
  }, [evaluatePanelGradientVisibility, item, isMobileLarge])

  useEffect(() => {
    if (!isMobileLarge) {
      setPanelGradientOffset(0)
      return
    }

    const footerEl = panelFooterRef.current

    if (!footerEl) {
      setPanelGradientOffset(0)
      return
    }

    const updateOffset = () => {
      setPanelGradientOffset(footerEl.offsetHeight || 0)
    }

    updateOffset()

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updateOffset)
      resizeObserver.observe(footerEl)

      return () => {
        resizeObserver.disconnect()
      }
    }

    return undefined
  }, [isMobileLarge, item])
  const toggle = (e) => {
    e.preventDefault()
    const isActive = panelRef.current.classList.contains(selectors.ACTIVE_CLASS)
    closeAllMenuSubPanels(item.level)
    const menuInnerContent = select(selectors.MEGA_MENI_INNER_CONTENT_SELECTOR)
    const menuSubPanelPrev = panelRef.current.closest(`.menu-sub-panel-${item.level - 1}`)
    if (!isActive) {
      addClass(selectors.ACTIVE_CLASS, panelRef.current)
      addClass(selectors.SUB_ACTIVE_CLASS, e.target)
      disableBodyScroll(panelInnerRef.current)

      if (item.level === 0) {
        addClass(`${selectors.SUB_ACTIVE_CLASS}-${item.level}`, menuInnerContent)
      } else if (item.level >= 1) {
        console.log(menuSubPanelPrev)
        addClass(`${selectors.SUB_ACTIVE_CLASS}-${item.level}`, menuSubPanelPrev)
      }
      if (isMobileLarge) {
        requestAnimationFrame(() => {
          evaluatePanelGradientVisibility()
        })
      } else {
        setShowPanelGradient(false)
        setPanelScrollIndicator(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev))
      }
    } else {
      removeClass(selectors.ACTIVE_CLASS, panelRef.current)
      removeClass(selectors.SUB_ACTIVE_CLASS, e.target)
      if (carouselInstance) {
        carouselInstance.slideTo(0)
      }
      clearAllBodyScrollLocks()
      if (item.level === 0) {
        const menuContent = select(selectors.MENU_CONTENT_SELECTOR)
        disableBodyScroll(menuContent)
        removeClass(`${selectors.SUB_ACTIVE_CLASS}-${item.level}`, menuInnerContent)
      } else if (item.level >= 1) {
        disableBodyScroll(panelInnerRef.current)
        const selectorPanelLevel = '.menu-sub-panel-content'
        const subPanelLevel = selectAll(selectorPanelLevel)
        map(disableBodyScroll, [...subPanelLevel])
        removeClass(`${selectors.SUB_ACTIVE_CLASS}-${item.level}`, menuSubPanelPrev)
      }
      setPanelScrollIndicator(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev))
    }
  }

  const closeMegaMenu = () => {
    setPanelScrollIndicator(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev))
    closeAllMenuSubPanels(selectors.LEVEL_DEFAULT)
    const megaMenu = selectAll(selectors.MEGA_MENU_WRAPPER_SELECTOR)
    const menuInnerContent = select(selectors.MEGA_MENI_INNER_CONTENT_SELECTOR)
    const menuSubPanelPrev = panelRef.current.closest(`.menu-sub-panel-${item.level - 1}`)
    removeClass(selectors.ACTIVE_CLASS, megaMenu)
    setTimeout(() => {
      removeClass(`${selectors.SUB_ACTIVE_CLASS}-0`, menuInnerContent)
      removeClass(`${selectors.SUB_ACTIVE_CLASS}-${item.level}`, menuInnerContent)
      if (carouselInstance) {
        carouselInstance.slideTo(0)
      }
    }, 400)
  }

  const options = {
    slidesPerView: 'auto',
    spaceBetween: 16,
    freeMode: {
      enabled: true,
      sticky: true
    },
    grabCursor: true,
    speed: 500,
    breakpoints: {
      1024: {
        slidesPerView: 4,
        spaceBetween: 24
      }
    }
  }

  const baseTrackTop = 60
  const footerOffset = Math.max(panelGradientOffset, 0)
  const panelScrollTrackStyle = isMobileLarge
    ? {
        top: `${baseTrackTop}px`,
        height: `calc(100% - ${baseTrackTop + footerOffset}px)`
      }
    : undefined
  return (
    <Fragment>
      <button className="justify-between menu-sub-title js-button-open-sub-menu" data-level={item.level} onclick={toggle}>
        <div className="flex items-center gap-4">
          {item?.block?.icon && (
            <div class="flex justify-center items-center truncate w-6 h-6">
              <Image
                imageUrl={[item?.block?.icon]}
                className="w-full h-auto"
                alt={`Submenu ${item?.block?.title}`}
                width="24"
                height="24"
              />
            </div>
          )}
          {item.title}
        </div>
        <Icon viewBox="0 0 16 16" name="icon-chevron-right-small" className="w-4 h-4 text-grey-500" />
      </button>
      <div ref={panelRef} className={`menu-sub-panel menu-sub-panel-${item.level} js-menu-sub-panel`} data-level={item.level}>
        <div className="container menu-sub-panel-container">
          <div className={`flex flex-col menu-sub-panel-inner menu-sub-panel-inner-${item.level} ${item.children.length > 8 ? 'scrollbar-custom-wrapper' : 'lg:!h-fit'}`}>
            <div className="menu-sub-panel-heading">
              <button className="menu-sub-panel-back" onclick={toggle} aria-label="Back sub panel">
                <Icon viewBox="0 0 20 20" name="icon-arrow-left" className="w-5 h-5 text-grey-500" />
              </button>
              <a href={item.url || '#'} title={item.title} className="menu-sub-panel-title">{item.title}</a>
              <button className="menu-sub-panel-close" title="Close sub panel" onclick={closeMegaMenu} aria-label="Close sub panel">
                <Icon name="icon-close" viewBox="0 0 20 20" strokeWidth="1.6" className='w-5 h-5 text-grey-500' />
              </button>
            </div>
            <div ref={panelInnerRef} className={'overflow-y-auto menu-sub-panel-content'}>
              <div className={`menu-sub-panel-content-inner ${item.children.length > 8 ? ' scrollbar-custom-inner' : ''}`}>
                {item?.children.map((item, index) => (
                  <MenuSubItem settings={settings} item={item} key={index} />
                ))}
              </div>
              {isMobileLarge && panelScrollIndicator.visible && (
                <div className="custom-scrollbar-track lg:hidden" aria-hidden="true" style={panelScrollTrackStyle}>
                  <span
                    className="custom-scrollbar-thumb"
                    style={{ height: `${panelScrollIndicator.height}px`, transform: `translateY(${panelScrollIndicator.top}px)` }}
                  ></span>
                </div>
              )}
              {showPanelGradient && (
                <div
                  className="menu-sub-panel-content-gradient lg:hidden"
                  aria-hidden="true"
                  style={{ bottom: `${panelGradientOffset}px` }}
                ></div>
              )}
            </div>
            {(item?.block?.image_1 || item?.block?.image_2) && (
              <div ref={panelFooterRef} className="px-4 py-6 bg-gray-100 menu-sub-panel-item">
                <div className="">
                  <CarouselSwiper
                    items={<FeatureImageItems item={item} />}
                    options={options}
                    navigation={true}
                    customNavigation={true}
                    scrollbar={false}
                    setCarouselInstance={setCarouselInstance}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  )
}

function FeatureImageItems({ item }) {
  return (
    <>
      {(item?.block?.image_1 && item?.block?.cta_url_1 && item?.block?.cta_text_1) && (
        <swiper-slide className="w-[250px]">
          <CardItem key={item?.block.image_1} ctaUrl={item?.block?.cta_url_1} ctaText={item?.block?.cta_text_1} imageUrl={item?.block.image_1}
            classImageWrapper="w-full h-[224px]"
            classImage="rounded-t w-full h-full object-cover"
            classTitleWrapper="flex justify-between items-center w-[250px] py-3 px-4 gap-2 bg-white rounded-b"
          />
        </swiper-slide>
      )}
      {(item?.block?.image_2 && item?.block?.cta_url_2 && item?.block?.cta_text_2) && (
        <swiper-slide className="w-[250px]">
          <CardItem key={item?.block.image_2} ctaUrl={item?.block?.cta_url_2} ctaText={item?.block?.cta_text_2} imageUrl={item?.block.image_2}
            classImageWrapper="w-full h-[224px]"
            classImage="rounded-t w-full h-full object-cover"
            classTitleWrapper="flex justify-between items-center w-[250px] py-3 px-4 gap-2 bg-white rounded-b"
          />
        </swiper-slide>
      )}
    </>
  )
}

function CardItem (props) {
  const { ctaUrl, ctaText, imageUrl, classImageWrapper, classImage, classTitleWrapper } = props
  return (
    <a href={ctaUrl} title={ctaText}>
      <Image
        imageUrl={[imageUrl]}
        className={classImageWrapper}
        imageClass={classImage}
        alt={`Card Item ${ctaText}`}
      />
      <div className={classTitleWrapper}>
        <span className="text-sm font-medium text-grey-1000">{ctaText}</span>
        <Icon viewBox="0 0 16 16" name="icon-chevron-right-small" className="w-4 h-4 text-grey-500" />
      </div>
    </a>
  )
}

function MenuSubAccordion ({ settings, item }) {
  return (
    <Fragment>
      <div className="menu-sub-title">
        {item.title}
      </div>
      <div className="">MenuSubAccordion</div>
    </Fragment>
  )
}

function MenuSubContent ({ item }) {
  const [classMenuSubMedia, setclassMenuSubMedia] = useState('')
  const menuSubContentInnerRef = useRef(null)
  const menuSubContentLeftRef = useRef(null)
  const shopByTitle = item?.block?.shop_by_title || ''
  const shopByLinks = item?.block?.shop_by_links || []

  const calcWidthShowImage = () => {
    const widthInnerContent = menuSubContentInnerRef.current?.offsetWidth
    const widthLeftContent = menuSubContentLeftRef.current?.offsetWidth
    if ((widthInnerContent - widthLeftContent) > 752) {
      setclassMenuSubMedia('menu-sub-content-media')
    } else {
      setclassMenuSubMedia('menu-sub-content-media menu-sub-content-single')
    }
  }

  useEffect(() => {
    calcWidthShowImage()
    on('resize', () => {
      calcWidthShowImage()
    }, window)
  }, [item])

  return (
    item?.children && item?.children.length > 0 && (
      <div className="menu-sub-content js-menu-sub-content">
        <div ref={menuSubContentInnerRef} className="menu-sub-content-inner">
          <div ref={menuSubContentLeftRef} class={`menu-sub-content-left ${item.children.length < 4 ? `menu-sub-content-left-${item.children.length}` : ''}`}>
            {
              item.children.map(menu => (
                <div class="flex flex-col text-left w-[164px] space-y-4">
                  <a className="menu-sub-content-headline" href={menu.url} title={menu.title}>
                    {menu.title}
                  </a>
                  {menu?.children && menu?.children.length > 0 && (
                    <div class="menu-sub-content-title-wrapper">
                      {menu.children.map(subMenu => (
                        <a className="menu-sub-content-title" href={subMenu.url} title={subMenu.title}>
                          {subMenu.title}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))
            }
          </div>
          <div className={`h-fit ${classMenuSubMedia}`}>
            {(item?.block?.image_desktop_1 && item?.block?.cta_url_desktop_1 && item?.block?.cta_text_desktop_1) && (
              <CardItem key={item?.block.image_desktop_1} ctaUrl={item?.block?.cta_url_desktop_1} ctaText={item?.block?.cta_text_desktop_1} imageUrl={item?.block.image_desktop_1}
                classImageWrapper="w-[348px] h-[270px] overflow-hidden"
                classImage="w-full h-full object-cover hover:zoom transition"
                classTitleWrapper="flex justify-between items-center w-[348px] py-4 gap-2 bg-white"
              />
            )}
            {(item?.block?.image_desktop_2 && item?.block?.cta_url_desktop_2 && item?.block?.cta_text_desktop_2) && (
              <CardItem key={item?.block.image_desktop_2} ctaUrl={item?.block?.cta_url_desktop_2} ctaText={item?.block?.cta_text_desktop_2} imageUrl={item?.block.image_desktop_2}
                classImageWrapper="w-[348px] h-[270px] overflow-hidden"
                classImage="w-full h-full object-cover hover:zoom transition"
                classTitleWrapper="flex justify-between items-center w-[348px] py-4 gap-2 bg-white"
              />
            )}
          </div>
        </div>
        {(shopByTitle && shopByLinks.length > 0) && (
          <div className="menu-sub-shop-by">
            <div className="shop-by-title">{shopByTitle}</div>
            <div className="shop-by-links">
              {shopByLinks.map(link => (
                <a className="menu-sub-content-title" href={link.url} title={link.title}>
                  {link.title}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  )
}

function MenuSecondary () {
  const menuSecondary = menuData?.secondMenu.map(menu => {
    return {
      title: menu.title,
      url: menu.url
    }
  })
  return (
    menuData?.secondMenu && menuData?.secondMenu.length > 0 && (
      <div className="px-4 py-6 bg-grey-100 lg:hidden">
        {menuSecondary.map(menu => (
          <a href={menu.url} title={menu.title} className="flex items-center mb-5 text-sm text-grey-700 last:mb-0">
            {menu.title}
          </a>
        ))}
      </div>
    )
  )
}

register(MegaMenu, 'mega-menu')
